import styled from 'styled-components';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormContent from 'components/layout/FormContent';
import { DocumentCopiesSearchRequest, DocumentCopiesSearchResponse } from 'ts-types/api.types';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { MessageSourceProps, withMessages } from 'react-message-source';
import axios from 'services/http';
import React, { useEffect, useState } from 'react';
import { BillStatus, BillType, DocumentCopiesSearchBy } from 'ts-types/api.enums';
import { FormApi } from 'final-form';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import moment from 'moment';
import { noop } from 'utils/functionUtils';
import FormContainer from 'components/layout/FormContainer';
import { Header, Icon } from 'semantic-ui-react';
import { applyStyles } from 'utils/textUtils';
import LoaderComponent from 'components/LoaderComponent';
import { searchCollectiveBills, searchDocumentCollectiveBills } from 'services/documentCopiesServices';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34,36,38,.15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(6) {
    text-align: right;
    padding-right: 0.5rem;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(7) {
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const TableContainer = styled.div`
  height: 90%;
  min-height: 300px;

  .collective-bill-icon {
     padding: 0 1rem;
  }
`;

const StyledForm = styled.form`
  height: 100%;
`;

const StyledFormContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`
   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-top: 2.2rem;
    margin-left: 20px;
  }

  .header-description {
    margin-bottom: 0rem;

    .header-button {
      margin-left: 1rem;
      vertical-align: top;
      background: transparent;
    }
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }
`;

const StyledSelectedBills = styled.span`
  float: right;
  text-align: right;
  font-weight: normal;
  font-size: 14px;

  &.margin-top {
    margin-top: 0.5em;
  }

  .highlight-value {
      color: rgb(48 145 218);
      font-weight: bold;
  }
`;

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const clickedRow = {
  background: "#b9b9bb",
  color: "rgba(1,1,1,1)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
}

interface ExtendedDocCopiesSearchResponse extends DocumentCopiesSearchResponse {
  selected: boolean,
  currentPaidAmount: number
}

interface FinalFormData {
  bills: Array<ExtendedDocCopiesSearchResponse>,
  selected?: ExtendedDocCopiesSearchResponse,
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  bills: Array<ExtendedDocCopiesSearchResponse>,
  billsLoading: boolean,
  onFilterBills: (documents: Array<DocumentCopiesSearchResponse>) => void,
  onBillsLoadingChange: (documentsLoading: boolean) => void,
  searchedId: number,
  onExpandedBill: (bill: any) => void,
  expandedBill: any
}

const cancelTokenSource = axios.CancelToken.source();

const CollectedBillsTableView = (props: Props) => {

  const {
    bills, billsLoading, currentUser, onFilterBills, onBillsLoadingChange, searchedId, expandedBill, onExpandedBill,
    getMessage, getMessageWithNamedParams
  } = props;

  const [formData, setFormData] = useState({} as Partial<FinalFormData>);
  const [partOfCollectionBillExpanded, setPartOfCollectionBillExpanded] = useState(false);

  useEffect(() => {
    const mappedBills: Array<ExtendedDocCopiesSearchResponse> = bills.map(bill => {

      const calculatedPaidAmount = ((bill.amount + bill.reminderSurcharge) - bill.paidAmount).toFixed(2);

      const selected = bill.status !== BillStatus.STORNO
          && bill.type === BillType.NORMAL;

      return {
        ...bill,
        selected: selected,
        currentPaidAmount: parseFloat(calculatedPaidAmount)
      }
    });

    setFormData({
      ...formData,
      bills: mappedBills,
      selected: undefined
    });
  }, [billsLoading])

  const billIconCellRenderer = (data: any) => {

    const {rowData} = data;
    const isCollectiveBill = rowData.type === BillType.COLLECTIVE;

    const showBillExpandIcon = searchedId
        ? searchedId === rowData.billId
        : expandedBill
            ? expandedBill.billId === rowData.billId
            : true;

    return (
        <>
          {showBillExpandIcon &&
              <Icon
                  className="collective-bill-icon"
                  name="plus"
                  size="small"
                  title={getMessage("collectiveBill.expandBill.iconTitle")}
                  onClick={() => showCollectiveBillInfo(rowData, isCollectiveBill)}
              />
          }
          {expandedBill && expandedBill.billId === rowData.billId &&
              <Icon
                  className="collective-bill-icon"
                  name="minus"
                  size="small"
                  title={getMessage("collectiveBill.hideBill.iconTitle")}
                  onClick={() => hideCollectiveBillInfo(rowData)}
              />

          }
        </>
    )
  };

  const billStatusCellRenderer = (data: any) => {

    const rowData: ExtendedDocCopiesSearchResponse = data.rowData;

    const billIconRequest: BillIconRequest = {
      hasReminders: false,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  const billDateCellRenderer = (data: any) => <div>{moment(data.rowData.date).format("DD.MM.YYYY")}</div>;
  const amountCellRenderer = (data: any) => <div>{(data.rowData.amount)}</div>;
  const paidAmountCellRenderer = (data: any) => <div>{(data.rowData.paidAmount)}</div>;

  const paymentDateCellRenderer = (data: any) => {
    if (data.rowData.lastPaymentDate) {
      return (
          <div>{moment(data.rowData.lastPaymentDate).format("DD.MM.YYYY")}</div>
      )
    }
  };

  const billsRowGetter = (bills: Array<ExtendedDocCopiesSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  const billsRowRenderer = (form: FormApi) => ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    const {bills = [], selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style, userSelect: "none"};

    if (selected && bills[index].billId == selected.billId) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    if (partOfCollectionBillExpanded &&
        expandedBill && bills[index].billId == expandedBill.billId) {
      styles = {
        ...styles,
        ...clickedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
        >
          {columns}
        </div>
    );
  };

  const showCollectiveBillInfo = (bill: ExtendedDocCopiesSearchResponse, collectiveBill: boolean) => {

    const mandateId = currentUser ? currentUser.userConfig.mandateId : undefined;
    const billId = collectiveBill ? parseInt(bill.billId, 10) : bill.collectiveBillId;

    onBillsLoadingChange(true);
    onExpandedBill(bill);
    setPartOfCollectionBillExpanded(!collectiveBill);

    searchDocumentCollectiveBills(billId, mandateId, cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        });
  };

  const hideCollectiveBillInfo = (bill: Partial<ExtendedDocCopiesSearchResponse>) => {

    onBillsLoadingChange(true);
    onExpandedBill(undefined);
    setPartOfCollectionBillExpanded(false);

    let request: Partial<DocumentCopiesSearchRequest> = {
      searchBy: DocumentCopiesSearchBy.BILL_NUMBER,
      fromBillNumber: bill.billId ? parseInt(bill.billId, 10) : 0,
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined
    }

    searchCollectiveBills(request, cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  }

  const renderForm = ({submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {bills = [], selected} = values;

    const countSelectedBills = bills.filter((bill: ExtendedDocCopiesSearchResponse) => bill.selected).length;
    const outputBtnDisabled = submitting || bills.length === 0 || countSelectedBills === 0;
    const billLength = bills.length;

    return (
        <StyledForm onSubmit={() => {
        }}>
          <FormContainer>
            <FormContent>
              <StyledFormContent>
                <StyledContainer style={{width: "50%"}}>
                  <Header>
                    {getMessage("bills")}
                    {!outputBtnDisabled &&

                        <StyledSelectedBills>
                          {
                            applyStyles(getMessage("documentCopies.selectedDocuments")
                                .replace("{{countSelectedBills}}", "" + countSelectedBills)
                                .replace("{{billsLength}}", "" + billLength)
                            )
                          }
                        </StyledSelectedBills>
                    }
                  </Header>
                  <TableContainer>
                    {billsLoading
                        ? <LoaderComponent
                            message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                        : renderBillsTable(form, bills)}
                  </TableContainer>
                </StyledContainer>
                <StyledContainer style={{width: "50%"}} className="margin">
                  <Header className={"header-description"}>
                    {getMessage("patient")}
                  </Header>
                  {selected && selected.patientName &&
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                        <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                      </Header>
                  }
                  <Header className={"header-description"}>
                    {getMessage("guarantor")}
                  </Header>
                  {selected && selected.guarantorDescription &&
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.guarantorDescription}`}</div>
                        <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                      </Header>
                  }
                  <Header className={"header-description"}>
                    {getMessage("bill.bank")}
                  </Header>
                  {selected && selected.bankInfo &&
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.bankInfo}`}</div>
                      </Header>
                  }
                  {selected && selected.type === BillType.PART_OF_COLLECTIVE &&
                      <React.Fragment>
                        <Header className={"header-description"}>
                          {getMessageWithNamedParams(
                              "documentCopies.showPartOfCollectiveBill.collectiveBillNumber",
                              {collectiveBillId: selected.collectiveBillId})}
                        </Header>
                      </React.Fragment>
                  }
                </StyledContainer>
              </StyledFormContent>
            </FormContent>
          </FormContainer>
        </StyledForm>
    );
  };

  const renderBillsTable = (form: FormApi, bills: Array<ExtendedDocCopiesSearchResponse>): React.ReactNode => {

    return (
        <StyledTable
            rowGetter={billsRowGetter(bills)}
            rowRenderer={billsRowRenderer(form)}
            rowCount={bills.length}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "selected",
                cellRenderer: billIconCellRenderer
              },
              {
                width: 80,
                label: getMessage("bill"),
                dataKey: "billStatus",
                cellRenderer: billStatusCellRenderer
              },
              {
                width: 120,
                label: getMessage("bill.date"),
                dataKey: "billDate",
                cellRenderer: billDateCellRenderer
              },
              {
                width: 90,
                label: "#",
                dataKey: "billId"
              },
              {
                width: 120,
                label: getMessage("bill.paymentDate"),
                dataKey: "billId",
                cellRenderer: paymentDateCellRenderer
              },
              {
                width: 130,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: amountCellRenderer
              },
              {
                width: 100,
                label: getMessage("bill.paidAmount"),
                dataKey: "paidAmount",
                cellRenderer: paidAmountCellRenderer
              }
            ]}
        />
    )
  };

  return (
      <StyledFinalFormContainer>
        <FinalForm
            onSubmit={noop}
            initialValues={formData}
            subscription={{values: true, pristine: true, submitting: true}}
            render={renderForm}
        />
      </StyledFinalFormContainer>
  );
}

export default withAuthContext(withMessages(CollectedBillsTableView));

